// Тонкий шар над Supabase RPC та Edge Functions. Усі рішення (доступ, перевірка, XP) — на сервері.
import { FunctionsHttpError } from '@supabase/supabase-js';
import type { PublicLesson } from '@shared/lessonSchema';
import { supabase } from './supabase';
import { ApiError } from './errors';

export type UnitStatus = 'completed' | 'in_progress' | 'available' | 'locked' | 'coming_soon' | 'paywall';
export interface PathUnit {
  id: string; kind: 'lesson' | 'check' | 'diagnostic'; seq: number; lessonNumber: number | null; title: string; isTrial?: boolean;
  blockId: string | null; blockTitle: string | null; blockOrder: number | null; status: UnitStatus;
  bestScore: number | null; maxScore: number | null; passed: boolean | null;
}
export interface SessionPayload {
  sessionId: string; lessonId: string; kind: 'lesson' | 'check' | 'diagnostic'; mode: 'learn' | 'assessment'; title: string; attemptsUsed?: number; maxAttempts?: number | null;
  lessonNumber: number | null; currentStep: number; status: 'in_progress' | 'completed';
  reviewStatus: 'proposed' | 'approved'; content: PublicLesson; solved: string[]; xpTotal: number;
}
export interface AnswerResult {
  correct?: boolean; attempt?: number; firstTry?: boolean; xpAwarded?: number; xpTotal?: number;
  explanation?: string | null; feedback?: string | null; alreadySolved?: boolean; recorded?: boolean;
}
export interface Badge { code: string; title: string; description: string; awardedAt?: string | null }
export interface ReviewItem { stepId: string; prompt: string; answer: unknown; correct: boolean; correctAnswer: string; explanation: string; feedback?: string | null; skill: string }
export interface CompletionSummary {
  kind: 'lesson' | 'check' | 'diagnostic'; lessonId: string; score: number; answersHidden?: boolean; attemptsUsed?: number; maxAttempts?: number | null; maxScore: number; passed: boolean;
  xpAwarded: number; xpTotal: number; streak: number; newBadges: Badge[]; review: ReviewItem[] | null; threshold: number | null;
  sessionId?: string; completedAt?: string;
  /** Пробний урок (ТЗ v5.4): без оплати після нього — екран «Вітаємо!» і шлях до оплати. */
  isTrial?: boolean; hasAccess?: boolean;
}
export interface ChildSummary {
  child: { id: string; displayName: string; avatar: string; grade: number | null; status: string };
  xpTotal: number; streak: number; lessonsCompleted: number; lessonsTotal: number; path: PathUnit[];
  access?: { hasAccess: boolean; paid: boolean; paywallEnabled: boolean };
  badges: Badge[]; activity: { day: string; active: boolean }[]; lastActivity: string | null;
  checks: { sessionId: string; lessonId: string; score: number; maxScore: number; passed: boolean; completedAt: string }[] | null;
  skills: { skill: string; firstTries: number; firstTryCorrect: number; enoughData: boolean }[] | null;
}
export interface StudioItem {
  id: string; kind: 'lesson' | 'check' | 'diagnostic'; seq: number; lessonNumber: number | null; title: string;
  blockId: string | null; blockTitle: string | null; blockOrder: number | null; enabled: boolean;
  version: number | null; reviewStatus: string | null; publishedAt: string | null;
  draftStatus: 'draft' | 'in_review' | 'changes_requested' | 'approved' | null; draftUpdatedAt: string | null; draftErrors: number; draftAuthor: string | null;
}
export interface StudioDoc {
  lesson: { id: string; kind: 'lesson' | 'check' | 'diagnostic'; title: string; lessonNumber: number | null; enabled: boolean; blockId: string | null };
  draft: any | null; draftStatus: StudioItem['draftStatus']; reviewNote: string | null; draftErrors: string[];
  published: any | null;
  versions: { version: number; reviewStatus: string; publishedAt: string; publishedBy: string; current: boolean }[] | null;
}
export interface ChildCard {
  id: string; displayName: string; avatar: string; grade: number | null; status: string; username: string;
  xpTotal: number; lessonsCompleted: number; lastActivity: string | null;
  trialCompleted?: boolean; paid?: boolean; hasAccess?: boolean;
}
export type Tokens = { access_token: string; refresh_token: string };
export interface CheckoutInfo {
  child: { id: string; displayName: string }; paid: boolean; paywallEnabled: boolean; paymentAvailable: boolean;
  parentName: string | null; phone: string | null; contactSent: boolean;
}
export interface AccessRow {
  id: string; username: string | null; childName: string; source: 'payment' | 'manual'; startsAt: string; endsAt: string | null;
  reason: string | null; revokedAt: string | null; revokeReason: string | null; createdAt: string; active: boolean;
}

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    // Батьки змінили пароль або призупинили доступ: стару сесію на цьому пристрої закриваємо (PAR-04).
    if (error.message.includes('session_revoked')) await supabase.auth.signOut({ scope: 'local' });
    throw new ApiError(error.message, error.message);
  }
  return data as T;
}

/** Замінити сесію на цьому пристрої (батьки → дитина): спершу закрити поточну, потім відкрити нову. */
async function switchSession(t: Tokens) {
  await supabase.auth.signOut({ scope: 'local' });
  const { error } = await supabase.auth.setSession(t);
  if (error) throw new ApiError(error.message);
}

async function fn<T>(name: string, body: Record<string, unknown> | FormData): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let code = 'server_error';
    if (error instanceof FunctionsHttpError) {
      try { code = (await error.context.json()).error ?? code; } catch { /* ignore */ }
    } else code = 'network';
    throw new ApiError(code);
  }
  return data as T;
}

export const api = {
  getPath: () => rpc<PathUnit[]>('get_path'),
  startSession: (lessonId: string) => rpc<SessionPayload>('start_session', { p_lesson: lessonId }),
  saveStep: (sessionId: string, step: number) => rpc<void>('save_step', { p_session: sessionId, p_step: step }),
  submitAnswer: (sessionId: string, stepId: string, answer: unknown, key: string, latencyMs: number) =>
    rpc<AnswerResult>('submit_answer', { p_session: sessionId, p_step: stepId, p_answer: answer, p_key: key, p_latency: latencyMs }),
  completeSession: (sessionId: string) => rpc<CompletionSummary>('complete_session', { p_session: sessionId }),
  sessionSummary: (sessionId: string) => rpc<CompletionSummary>('get_session_summary', { p_session: sessionId }),
  childSummary: (childId?: string) => rpc<ChildSummary>('get_child_summary', childId ? { p_child: childId } : {}),
  myChildren: () => rpc<ChildCard[]>('get_my_children'),
  updateChild: (childId: string, displayName: string, grade: number | null, avatar: string) =>
    rpc<void>('parent_update_child', { p_child: childId, p_display_name: displayName, p_grade: grade, p_avatar: avatar }),
  setChildStatus: (childId: string, status: 'active' | 'suspended') =>
    rpc<void>('parent_set_child_status', { p_child: childId, p_status: status }),
  adminOverview: () => rpc<any>('admin_overview'),
  adminSetLessonEnabled: (lessonId: string, enabled: boolean) => rpc<void>('admin_set_lesson_enabled', { p_lesson: lessonId, p_enabled: enabled }),
  adminRetryOutbox: (id: number) => rpc<void>('admin_retry_outbox', { p_id: id }),

  // ── Конструктор і кабінет викладача ──
  studioList: () => rpc<StudioItem[]>('studio_list'),
  studioGet: (lessonId: string) => rpc<StudioDoc>('studio_get', { p_lesson: lessonId }),
  studioSave: (lessonId: string, source: unknown) => rpc<{ errors: string[]; status: string }>('studio_save', { p_lesson: lessonId, p_source: source }),
  studioSubmit: (lessonId: string) => rpc<void>('studio_submit', { p_lesson: lessonId }),
  studioReview: (lessonId: string, approve: boolean, note: string) => rpc<void>('studio_review', { p_lesson: lessonId, p_approve: approve, p_note: note }),
  studioPublish: (lessonId: string, enable: boolean) => rpc<{ version: number; unchanged: boolean }>('studio_publish', { p_lesson: lessonId, p_enable: enable }),
  studioRollback: (lessonId: string, version: number) => rpc<void>('studio_rollback', { p_lesson: lessonId, p_version: version }),
  studioDraftFromVersion: (lessonId: string, version: number) => rpc<{ errors: string[] }>('studio_draft_from_version', { p_lesson: lessonId, p_version: version }),
  studioSetTitle: (lessonId: string, title: string) => rpc<void>('studio_set_title', { p_lesson: lessonId, p_title: title }),
  // Медіатека: файл зберігається на сервері (функція media), посилання — /media/… на тому ж домені.
  mediaUpload: (file: File, title?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    return fn<{ id: string; url: string; kind: string; title: string }>('media', form);
  },
  mediaDelete: (id: string) => fn<{ ok: true }>('media', { action: 'delete', id }),
  teacherStudents: () => rpc<(ChildCard & { streak: number; firstTryRate: number | null })[]>('teacher_my_students'),
  adminSetRole: (email: string, role: string, grant: boolean) => rpc<void>('admin_set_role', { p_email: email, p_role: role, p_grant: grant }),
  adminAssignChild: (teacherEmail: string, username: string, assign: boolean) => rpc<void>('admin_assign_child', { p_teacher_email: teacherEmail, p_username: username, p_assign: assign }),
  adminListStaff: () => rpc<{ userId: string; email: string; name: string; role: string; students: number }[]>('admin_list_staff'),

  createChild: (body: { displayName: string; grade: number | null; avatar: string; password: string }) =>
    fn<{ id: string; username: string }>('child-create', body),
  resetChild: (childId: string, password: string) => fn<{ ok: true }>('child-reset', { childId, password }),
  childLogin: async (username: string, password: string) => {
    const t = await fn<{ access_token: string; refresh_token: string }>('child-login', { username, password });
    const { error } = await supabase.auth.setSession(t);
    if (error) throw new ApiError(error.message);
  },
  captureLead: (body: Record<string, unknown>) => fn<{ ok: true }>('lead-capture', body),

  // ── ТЗ v5.4: пробний урок, оплата, доступ ──
  startConfig: () => rpc<{ trialAvailable: boolean; paywallEnabled: boolean }>('get_start_config'),
  trialSignup: (body: { email: string; childName: string; consentTerms: boolean; consentChildData: boolean; intent: string; utm: Record<string, string>; consentVersion: string }) =>
    fn<{ ok: true }>('trial-signup', body),
  /** Код з листа: пробна форма → сесія дитини (або батьків, якщо треба обрати дитину); вхід → сесія батьків. */
  verifyCode: async (email: string, code: string, purpose: 'trial' | 'login', childName?: string) => {
    const r = await fn<{ child?: Tokens; parent?: Tokens; childId?: string; chooseChild?: boolean; needChild?: boolean }>('trial-verify', { email, code, purpose, childName });
    await switchSession((r.child ?? r.parent)!);
    return { asChild: Boolean(r.child), chooseChild: Boolean(r.chooseChild), needChild: Boolean(r.needChild) };
  },
  /** Код входу батькам на email. Відповідь однакова для будь-якого email (REG-09). */
  sendLoginCode: async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
    if (error && (error.status === 429 || /rate/i.test(error.message))) throw new ApiError('over_email_send_rate_limit');
  },
  openChild: async (childId: string) => switchSession(await fn<Tokens>('child-session', { childId })),
  sendChildAccess: async () => {
    const r = await fn<{ username: string; password?: string; sent: boolean; emailed?: number; session?: Tokens }>('send-child-access', {});
    // Новий пароль завершив попередні сесії дитини — продовжуємо в новій, виданій сервером.
    if (r.session) { const { error } = await supabase.auth.setSession(r.session); if (error) throw new ApiError(error.message); }
    return r;
  },
  notifyParentCheckout: (source: string) => fn<{ ok: true; sent: number }>('notify-parent-checkout', { source }),
  checkoutOpen: (childId: string, source: string) => rpc<CheckoutInfo>('checkout_open', { p_child: childId, p_source: source }),
  checkoutContact: (name: string, phone: string) => rpc<void>('checkout_contact', { p_name: name, p_phone: phone }),
  adminGrantAccess: (username: string, reason: string, days: number | null) => rpc<string>('admin_grant_access', { p_username: username, p_reason: reason, p_days: days }),
  adminRevokeAccess: (username: string, reason: string) => rpc<number>('admin_revoke_access', { p_username: username, p_reason: reason }),
  adminListAccess: () => rpc<AccessRow[]>('admin_list_access'),
  adminSetSetting: (key: string, value: unknown) => rpc<void>('admin_set_setting', { p_key: key, p_value: value }),
};

/** Ключ ідемпотентності для відповіді (EX-01). */
export const idemKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

/** Повтор при мережевих збоях (EX-01): до 3 повторів із паузою 0,6 / 1,2 / 2,4 с. Безпечно завдяки ключу ідемпотентності. */
export async function withRetry<T>(f: () => Promise<T>, retries = 3): Promise<T> {
  for (let i = 0; ; i++) {
    try { return await f(); } catch (e) {
      const code = e instanceof ApiError ? e.code : String(e);
      if (i >= retries || !/fetch|network|timeout|502|503|504/i.test(code)) throw e;
      await new Promise((r) => setTimeout(r, 600 * 2 ** i));
    }
  }
}
